/**
 * Runtime entry. Wires the bottom bar, sheets, language toggle, footnote
 * popover, scroll restoration, and service-worker registration. All content
 * lives in the static HTML — this script only adds behavior.
 */
import { load, save, apply, lastArticle, type Prefs } from './prefs.ts';

declare global {
  interface Window { __mhArticleIds?: string[]; }
}

const prefs: Prefs = load();
apply(prefs);

// ---------- Element refs ----------
const root = document.documentElement;
const reader = document.getElementById('reader')!;
const sheets = {
  toc: document.getElementById('sheet-toc') as HTMLDialogElement,
  opts: document.getElementById('sheet-opts') as HTMLDialogElement,
  search: document.getElementById('sheet-search') as HTMLDialogElement,
};
const footnotePopover = document.getElementById('footnote-popover') as HTMLDialogElement;

// ---------- Sheet open/close ----------
function openSheet(which: keyof typeof sheets): void {
  const d = sheets[which];
  if (!d.open) d.showModal();
  if (which === 'search') ensureSearchReady();
}
function closeAnySheet(): void {
  for (const d of Object.values(sheets)) if (d.open) d.close();
  if (footnotePopover.open) footnotePopover.close();
}

// Dismiss on backdrop click
for (const d of [...Object.values(sheets), footnotePopover]) {
  d.addEventListener('click', (e) => {
    const rect = d.getBoundingClientRect();
    if (e.clientY < rect.top || e.clientY > rect.bottom || e.clientX < rect.left || e.clientX > rect.right) {
      d.close();
    }
  });
}

// ---------- Bottom bar ----------
document.getElementById('bottombar')!.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement).closest<HTMLElement>('[data-sheet],[data-action]');
  if (!target) return;
  const sheet = target.dataset.sheet as keyof typeof sheets | undefined;
  if (sheet) openSheet(sheet);
  const action = target.dataset.action;
  if (action === 'lang') toggleLang();
});

// Sheet close buttons / pref segment buttons
document.body.addEventListener('click', (e) => {
  const el = e.target as HTMLElement;
  const closer = el.closest<HTMLElement>('[data-action="close-sheet"]');
  if (closer) { closeAnySheet(); return; }
});

// ---------- Language toggle ----------
function toggleLang(): void {
  setLang(prefs.lang === 'en' ? 'fr' : 'en');
}
function setLang(next: 'en' | 'fr'): void {
  const before = currentVisibleArticleId();
  prefs.lang = next;
  apply(prefs);
  save(prefs);
  if (before) {
    // Realign: language text-height delta would otherwise drift.
    const el = document.getElementById(before);
    el?.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior });
  }
}

// ---------- Reading options ----------
function syncOptsUI(): void {
  for (const fs of sheets.opts.querySelectorAll<HTMLFieldSetElement>('.opts-row')) {
    const pref = fs.dataset.pref as keyof Prefs;
    const value = String(prefs[pref]);
    for (const btn of fs.querySelectorAll<HTMLButtonElement>('.seg-btn')) {
      btn.classList.toggle('is-active', btn.dataset.value === value);
    }
  }
}
sheets.opts.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg-btn');
  if (!btn) return;
  const fs = btn.closest<HTMLFieldSetElement>('.opts-row');
  if (!fs) return;
  const pref = fs.dataset.pref as keyof Prefs;
  const value = btn.dataset.value;
  if (!pref || !value) return;
  (prefs as any)[pref] = value;
  apply(prefs);
  save(prefs);
  syncOptsUI();
});
syncOptsUI();
sheets.opts.addEventListener('toggle', syncOptsUI);

// ---------- TOC nav: close sheet then smooth-scroll ----------
sheets.toc.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
  if (!a) return;
  e.preventDefault();
  const id = decodeURIComponent(a.getAttribute('href')!.slice(1));
  sheets.toc.close();
  scrollToArticle(id, 'smooth');
});

function scrollToArticle(id: string, behavior: ScrollBehavior = 'smooth'): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: 'start', behavior });
  // briefly flash
  el.classList.add('flash');
  setTimeout(() => el.classList.remove('flash'), 900);
}

// ---------- Hash routing / scroll restoration ----------
function restoreInitial(): void {
  const hash = decodeURIComponent(location.hash.slice(1));
  if (hash) {
    if (hash.startsWith('p-')) {
      const num = parseInt(hash.slice(2), 10);
      const p = document.querySelector<HTMLElement>(`p[data-para="${num}"]`);
      if (p) {
        const a = p.closest('article');
        a?.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior });
        return;
      }
    }
    if (document.getElementById(hash)) {
      scrollToArticle(hash, 'instant' as ScrollBehavior);
      return;
    }
  }
  const last = lastArticle.get();
  if (last && document.getElementById(last)) scrollToArticle(last, 'instant' as ScrollBehavior);
}
restoreInitial();

// ---------- IntersectionObserver: which article is "current"? ----------
let currentId: string | null = null;
const articles = Array.from(document.querySelectorAll<HTMLElement>('main#reader > article'));
function currentVisibleArticleId(): string | null { return currentId; }

let pendingHashUpdate: number | null = null;
function scheduleHashUpdate(id: string): void {
  if (pendingHashUpdate) cancelAnimationFrame(pendingHashUpdate);
  pendingHashUpdate = requestAnimationFrame(() => {
    if (currentId !== id) return;
    if (location.hash !== `#${id}`) history.replaceState(null, '', `#${id}`);
    lastArticle.set(id);
  });
}

const obs = new IntersectionObserver((entries) => {
  // Pick the entry with the largest intersection ratio that's intersecting.
  let best: IntersectionObserverEntry | null = null;
  for (const e of entries) if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
  // Also look across all currently-intersecting tracked articles
  // (entries only includes those whose state just changed).
  // We'll use the heuristic of: the topmost article whose top is above 50% of viewport.
  let topmost: HTMLElement | null = null;
  const vh = window.innerHeight;
  for (const a of articles) {
    const r = a.getBoundingClientRect();
    if (r.top <= vh * 0.4 && r.bottom > 60) { topmost = a; break; }
  }
  const chosen = topmost ?? (best?.target as HTMLElement | undefined) ?? null;
  if (chosen && chosen.id !== currentId) {
    currentId = chosen.id;
    scheduleHashUpdate(currentId);
  }
}, { threshold: [0, 0.25, 0.5, 0.75, 1] });
for (const a of articles) obs.observe(a);

// ---------- Footnote popover ----------
reader.addEventListener('click', (e) => {
  const ref = (e.target as HTMLElement).closest<HTMLElement>('sup.fn-ref > a');
  if (!ref) return;
  e.preventDefault();
  const num = ref.dataset.fn;
  if (!num) return;
  const fn = document.getElementById(`fn-${num}`);
  if (!fn) return;
  const body = footnotePopover.querySelector('.popover-body')!;
  body.innerHTML = fn.innerHTML;
  // Remove the back-ref arrow (it's noise in the popover)
  body.querySelector('.fn-backref')?.remove();
  if (!footnotePopover.open) footnotePopover.showModal();
});

// ---------- Search (lazy) ----------
let searchReady = false;
async function ensureSearchReady(): Promise<void> {
  if (searchReady) return;
  searchReady = true;
  const mod = await import('./search.ts');
  mod.init(sheets.search, () => prefs.lang, (articleId, num) => {
    sheets.search.close();
    const el = document.getElementById(articleId);
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    if (num) {
      const p = el.querySelector<HTMLElement>(`p[data-para="${num}"]`);
      if (p) {
        // After the article snap, scroll the paragraph into view too.
        setTimeout(() => p.scrollIntoView({ block: 'center', behavior: 'smooth' }), 320);
        p.classList.add('flash');
        setTimeout(() => p.classList.remove('flash'), 1500);
      }
    }
  });
}

// ---------- Service worker ----------
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* silent on first dev runs */ });
  });
}

// ---------- Esc to dismiss sheets ----------
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeAnySheet();
});
